import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../components/ui/drawer";
import { api } from "../lib/api";
import { useWarehouses } from "../lib/useWarehouses";
import { EMPTY_FILTERS, activeFilterCount } from "./catalogFilters";

const inputCls = "w-full h-10 px-3 rounded-[10px] border border-[#E7E5E4] bg-white text-sm text-[#1C1917] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 font-normal";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-[#78716C] mb-1">{label}</span>
      {children}
    </label>
  );
}

// Text input with server suggestions (datalist) — mirrors the desktop AsyncAutocompleteField.
function SuggestInput({ value, onChange, endpoint, testId }) {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api.get(endpoint, { params: { q: value || undefined, limit: 15 }, signal: controller.signal })
        .then(({ data }) => setOptions(data.items || [])).catch(() => {});
    }, 150);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [value, endpoint]);
  const listId = `${testId}-list`;
  return (
    <>
      <input list={listId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} autoComplete="off" />
      <datalist id={listId}>{options.map((o) => <option key={o} value={o} />)}</datalist>
    </>
  );
}

function Range({ min, max, onMin, onMax, testId }) {
  return (
    <div className="flex items-center gap-2">
      <input type="number" inputMode="numeric" placeholder="Min" value={min ?? ""} onChange={(e) => onMin(e.target.value)} className={inputCls} data-testid={`${testId}-min`} />
      <span className="text-[#A8A29E]">–</span>
      <input type="number" inputMode="numeric" placeholder="Max" value={max ?? ""} onChange={(e) => onMax(e.target.value)} className={inputCls} data-testid={`${testId}-max`} />
    </div>
  );
}

export function MobileFilterSheet({ open, onOpenChange, filters, setFilters, categoryOptions = [], warehouseScope = null }) {
  const { t } = useTranslation();
  const { warehouses } = useWarehouses();
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const count = activeFilterCount(filters);
  const F = (k) => t(`dashboard.filters.${k}`);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92vh]" data-testid="mobile-filter-sheet">
        <DrawerHeader className="flex items-center justify-between pb-2">
          <DrawerTitle className="text-base">{t("mobile.filters")}{count > 0 && <span className="ml-2 text-xs font-semibold text-white bg-[#1E3A8A] rounded-full px-2 py-0.5" data-testid="mobile-filter-sheet-count">{count}</span>}</DrawerTitle>
          <button type="button" onClick={() => setFilters({ ...EMPTY_FILTERS, q: filters.q })} className="text-sm font-medium text-[#1E3A8A]" data-testid="mobile-filter-reset">{t("common.reset")}</button>
        </DrawerHeader>
        <div className="overflow-y-auto px-4 pb-6 space-y-3">
          <Field label={F("budget_no")}><input value={filters.budget_no} onChange={(e) => set("budget_no", e.target.value)} className={inputCls} data-testid="mobile-filter-budget-no" /></Field>
          <Field label={F("factory")}><SuggestInput value={filters.factory_name} onChange={(v) => set("factory_name", v)} endpoint="/inventory/factory-suggestions" testId="mobile-filter-factory" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={F("purchaser")}><input value={filters.purchaser} onChange={(e) => set("purchaser", e.target.value)} className={inputCls} data-testid="mobile-filter-purchaser" /></Field>
            <Field label={F("external_sales")}><input value={filters.external_sales} onChange={(e) => set("external_sales", e.target.value)} className={inputCls} data-testid="mobile-filter-external-sales" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={F("contract_no")}><SuggestInput value={filters.contract_no} onChange={(v) => set("contract_no", v)} endpoint="/inventory/contract-no-suggestions" testId="mobile-filter-contract-no" /></Field>
            <Field label={F("invoice_no")}><SuggestInput value={filters.invoice_no} onChange={(v) => set("invoice_no", v)} endpoint="/inventory/invoice-no-suggestions" testId="mobile-filter-invoice-no" /></Field>
          </div>
          <Field label={F("tag")}><SuggestInput value={filters.tag} onChange={(v) => set("tag", v)} endpoint="/inventory/tag-suggestions" testId="mobile-filter-tag" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={F("category")}>
              <select value={filters.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="mobile-filter-category">
                <option value="">{t("mobile.all")}</option>
                {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={F("product_type")}>
              <select value={filters.product_type} onChange={(e) => set("product_type", e.target.value)} className={inputCls} data-testid="mobile-filter-product-type">
                <option value="">{t("mobile.all")}</option>
                <option value="CI产品">CI Product</option>
                <option value="拼箱产品">Consolidated Shipment</option>
                <option value="拼单">Pooled Order</option>
              </select>
            </Field>
          </div>
          <Field label={F("approval")}>
            <select value={filters.approval} onChange={(e) => set("approval", e.target.value)} className={inputCls} data-testid="mobile-filter-approval">
              <option value="">{t("mobile.all")}</option>
              <option value="yes">{t("mobile.approved")}</option>
              <option value="no">{t("mobile.rejected")}</option>
              <option value="empty">{t("mobile.pending")}</option>
            </select>
          </Field>
          <Field label={F("quantity_range")}><Range min={filters.min_qty} max={filters.max_qty} onMin={(v) => set("min_qty", v)} onMax={(v) => set("max_qty", v)} testId="mobile-filter-qty" /></Field>
          <Field label={F("shipping_qty_range")}><Range min={filters.min_shipping_qty} max={filters.max_shipping_qty} onMin={(v) => set("min_shipping_qty", v)} onMax={(v) => set("max_shipping_qty", v)} testId="mobile-filter-shipping-qty" /></Field>
          <Field label={F("ci_qty_range")}><Range min={filters.min_ci_qty} max={filters.max_ci_qty} onMin={(v) => set("min_ci_qty", v)} onMax={(v) => set("max_ci_qty", v)} testId="mobile-filter-ci-qty" /></Field>
          <div className="grid grid-cols-2 gap-3 items-end">
            <Field label={F("has_samples")}>
              <button type="button" role="switch" aria-checked={!!filters.has_samples} onClick={() => set("has_samples", !filters.has_samples)} data-testid="mobile-filter-has-samples"
                className={`relative h-10 w-16 rounded-full transition-colors ${filters.has_samples ? "bg-[#1E3A8A]" : "bg-[#D6D3D1]"}`}>
                <span className={`absolute top-1 h-8 w-8 rounded-full bg-white shadow transition-transform ${filters.has_samples ? "translate-x-7" : "translate-x-1"}`} />
              </button>
            </Field>
            <Field label={F("sample_warehouse")}>
              <select value={filters.sample_warehouse_id} onChange={(e) => set("sample_warehouse_id", e.target.value)} className={inputCls} data-testid="mobile-filter-warehouse">
                <option value="">{warehouseScope ? t("dashboard.filters.follow_quote") : t("dashboard.filters.all_warehouses")}</option>
                {warehouses.filter((w) => w.active || w.id === filters.sample_warehouse_id).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("mobile.date_type")}>
            <select value={filters.date_field || "contract_date"} onChange={(e) => set("date_field", e.target.value)} className={inputCls} data-testid="mobile-filter-date-field">
              <option value="contract_date">Contract Date</option>
              <option value="delivery_date">Delivery Date</option>
              <option value="shipping_date">Shipping Date</option>
              <option value="po_shipping_date">PO Shipping Date</option>
            </select>
          </Field>
          <Field label={t("mobile.date_range")}>
            <div className="flex items-center gap-2">
              <input type="date" value={filters.date_from || ""} onChange={(e) => set("date_from", e.target.value)} className={inputCls} data-testid="mobile-filter-date-from" />
              <span className="text-[#A8A29E]">–</span>
              <input type="date" value={filters.date_to || ""} onChange={(e) => set("date_to", e.target.value)} className={inputCls} data-testid="mobile-filter-date-to" />
            </div>
          </Field>
          <button type="button" onClick={() => onOpenChange(false)} className="press-scale w-full h-12 rounded-[12px] bg-[#1E3A8A] text-white font-semibold text-sm mt-2" data-testid="mobile-filter-apply">
            {t("mobile.show_results")}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
